import { useLocation } from 'react-router-dom';

import { useActivation } from '../ActivationContext';

import { isAllowedAppHandoffTarget } from '@/utils/urlHelpers';
import { REDIRECT_TO_SESSION_KEY, CANOPY_POST_AUTH_CALLBACK, HANDOFF_NONCE_PARAM } from '@/utils/constants';
import { logError } from '@/utils/logError';
import {
  CLIENT_PARAM,
  CLIENT_SESSION_KEY,
  addressForClient,
} from '@/utils/clientRegistry';

/**
 * Where to send the member's device once authentication finishes.
 *
 * **Why this is looked up rather than held in the flow's state.** The app opens the
 * portal inside a browser and watches for it to navigate to a private address carrying a
 * one-time code. If that address is lost anywhere along the way, the member completes
 * every step successfully and the app never finds out — they are left looking at a
 * browser that goes nowhere.
 *
 * The flow's in-memory state is not a reliable place to keep it. A member who arrives by
 * following a link from their email lands in a **fresh browser tab**, so the state built
 * up before the email was sent is gone, and the address with it. The screens after email
 * verification run in exactly that case.
 *
 * So the address is looked for in three places, most specific first: this screen's own
 * URL, the copy saved when the member first arrived, and whatever the flow's state still
 * holds.
 *
 * **Every candidate must be one of the two addresses a session may be handed to**, checked
 * with `isAllowedAppHandoffTarget`, not merely a URI with a scheme we recognise. Two reasons.
 * A scheme check admits `sleepio://anything?x=1`, and this value ends up carrying a one-time
 * token. And the auth service refuses anything but an exact match, so relaying a
 * scheme-valid-but-unlisted address would make it answer 400 and the member would get no
 * email at all — a failure caused entirely by us being more permissive than the service we
 * are talking to. The saved copy makes that reachable without an attacker: the storage key is
 * shared with the Spark flows, so a member who touches a Spark entry point and then this one
 * in the same tab has a `spark://…` value sitting there.
 *
 * A candidate that fails the check is **ignored, not relayed** — the send then succeeds
 * without an address instead of failing — and the decision is logged, because an address
 * silently not honoured is the exact mechanism that hid PAT-1286.
 *
 * There is one lookup here, in two forms, differing only in what happens when none of the
 * three places has an address:
 *
 * - `appSuppliedRedirectTo` returns nothing. Use it when relaying the address onward to
 *   the auth service, where absent has to stay absent.
 * - `resolvePostAuthRedirectTo` returns the app's single post-authentication address. Use
 *   it at the end of the flow, where some address has to be chosen.
 *
 * **That last step is a default, not a decision.** An earlier version chose between two
 * addresses depending on whether it believed the member was new or returning. Guess wrong
 * and a returning member was sent to a screen that needs a credential it was never given,
 * landing them back on sign-up (PAT-1286). There is now one address, so there is nothing
 * to guess — and no intent to guess it from.
 */

interface RedirectToSources {
  /** The current screen's query string, e.g. `useLocation().search`. */
  search: string;
  /** Whatever the activation flow's state still holds, when a caller has that state. */
  fromFlowState?: string | null;
}

/**
 * The scheme of a value, for logging. Never log the value itself — a rejected address can
 * carry arbitrary query params, and this runs on paths a member's own data passes through.
 */
const schemeOf = (value: string): string => {
  const idx = value.indexOf(':');
  return idx === -1 ? '(none)' : value.slice(0, idx + 1);
};

/**
 * Values already reported. These resolvers are called from render, so without this a single
 * screen would log once per frame. Page-scoped and bounded by the number of distinct bad
 * values a page sees, which is one in every case we know of.
 */
const reportedRejections = new Set<string>();
let reportedDefaultFallback = false;

const reportRejected = (field: string, value: string): void => {
  const seen = `${field}:${value}`;
  if (reportedRejections.has(seen)) { return; }
  reportedRejections.add(seen);
  logError(
    '[PostAuthRedirect]',
    'ignored a redirect address that is not one of the app handoff targets',
    undefined,
    { level: 'warning', extra: { field, scheme: schemeOf(value) } },
  );
};

/**
 * The address the app actually asked for, or nothing at all.
 *
 * Returning nothing matters when we relay this to the auth service: claiming an app
 * address for a member who is signing in on a desktop browser would put a private app
 * link in their email that their machine cannot open. Absent means absent.
 */
/**
 * The client name this flow was opened with, from the URL or from the copy saved when it
 * started. Undefined for a client that never named itself, or a name we do not recognise.
 *
 * Read before the address below, because a name is the thing that actually survives the
 * journey. The address is a nested, thrice-encoded URL and has been lost in a real sign-in;
 * the name is a short flat scalar of the same shape as `org_slug` and `flow`, which arrived
 * intact in that same sign-in.
 */
const suppliedClient = (search: string): string | undefined => {
  const fromUrl = new URLSearchParams(search).get(CLIENT_PARAM);
  if (fromUrl) { return fromUrl; }
  // Guarded for the same reason the storage read below is: a browser with storage locked down
  // throws on access and this app has no error boundary.
  try {
    return sessionStorage.getItem(CLIENT_SESSION_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

export const appSuppliedRedirectTo = (
  { search, fromFlowState }: RedirectToSources,
): string | undefined => {
  // The client name first. Resolving a name we hold the address for is strictly better than
  // trusting an address that travelled — it cannot be mangled in transit, and an unknown name
  // resolves to nothing rather than to somewhere unexpected.
  const fromClient = addressForClient(suppliedClient(search));
  if (fromClient) { return fromClient; }

  const fromUrl = new URLSearchParams(search).get('redirectTo');
  if (fromUrl) {
    if (isAllowedAppHandoffTarget(fromUrl)) { return fromUrl; }
    reportRejected('url', fromUrl);
  }

  // Guarded because this also runs in a render path. A browser with storage locked down
  // throws on access, and there is no error boundary in this app — an unguarded read here
  // blanks the screen mid-flow. Treat unreadable storage as "no saved value" and fall
  // through to the remaining candidates.
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);
  } catch {
    stored = null;
  }
  if (stored) {
    if (isAllowedAppHandoffTarget(stored)) { return stored; }
    // Reachable without an attacker: this key is shared with the Spark flows.
    reportRejected('session_storage', stored);
  }

  if (fromFlowState) {
    if (isAllowedAppHandoffTarget(fromFlowState)) { return fromFlowState; }
    reportRejected('flow_state', fromFlowState);
  }

  return undefined;
};

/**
 * The same lookup, with the app's single post-authentication address as the answer when
 * nothing was supplied. Kept free of React context so the callbacks that run outside the
 * activation provider resolve the address exactly the same way as the screens inside it.
 */
export const resolvePostAuthRedirectTo = (
  sources: RedirectToSources,
): string => {
  const supplied = appSuppliedRedirectTo(sources);
  if (supplied) { return supplied; }

  // The address did not arrive. Before falling back, look for the handoff nonce on its own.
  //
  // The auth service sends the nonce twice: once inside the address, and once as a flat scalar
  // parameter beside `flow`. That redundancy is the point. The nested copy has to survive the
  // tracking link, the verify redirect and the callback, and in a real sign-in it did not,
  // while the scalars at the same depth came through untouched. Falling back to the bare
  // default in that case is not a degraded handoff — it is a dead end, because the app refuses
  // any handoff whose nonce it did not mint. Re-attaching the flat copy is what makes the
  // fallback work again.
  //
  // Not a security decision. This value is only ever compared, on the device, against the set
  // that install minted and stored; a nonce nobody minted matches nothing. See
  // capHandoffNonce.native.ts in canopy for the invariant.
  const flatNonce = new URLSearchParams(sources.search).get(HANDOFF_NONCE_PARAM);

  if (!reportedDefaultFallback) {
    reportedDefaultFallback = true;
    logError(
      '[PostAuthRedirect]',
      'no app address supplied; using the default post-auth callback',
      undefined,
      // `recoveredNonce` is what tells the two cases apart in a trace: a fallback that can still
      // complete, and one that cannot. Never the value — only whether there was one.
      { level: 'warning', extra: { source: 'default', recoveredNonce: Boolean(flatNonce) } },
    );
  }

  if (flatNonce) {
    return `${CANOPY_POST_AUTH_CALLBACK}?${HANDOFF_NONCE_PARAM}=${encodeURIComponent(flatNonce)}`;
  }
  return CANOPY_POST_AUTH_CALLBACK;
};

/** Test seam: the report-once latches are module state by design. */
export const resetRedirectReportingForTests = (): void => {
  reportedRejections.clear();
  reportedDefaultFallback = false;
};

/**
 * The hook form, for screens mounted inside the activation provider. It supplies the two
 * inputs the lookup needs — this screen's query string and the flow's state — and returns
 * the resolved address.
 */
export const usePostAuthRedirectTo = (): string => {
  const { search } = useLocation();
  const { state } = useActivation();

  return resolvePostAuthRedirectTo({ search, fromFlowState: state.redirectTo });
};

/**
 * The hook form of the relay lookup, for screens inside the provider that need to pass
 * the address to the auth service rather than navigate to it.
 */
export const useAppSuppliedRedirectTo = (): string | undefined => {
  const { search } = useLocation();
  const { state } = useActivation();

  return appSuppliedRedirectTo({ search, fromFlowState: state.redirectTo });
};

/**
 * The client name this flow was opened with, for the screens that relay it to the auth
 * service. Undefined when no client named itself.
 *
 * Deliberately NOT resolved to an address here. The auth service holds its own copy of the
 * registry and resolves the name itself — that is the whole point of sending a name, and
 * sending an address instead would put the fragile thing back on the wire.
 */
export const useSuppliedClient = (): string | undefined => {
  const { search } = useLocation();
  return suppliedClient(search);
};
